<template>
  <s-modal
    v-model="showModal"
    modal-class="alarm-edit-modal"
    centered
    ok-confirm-message-code="MC011"
    :confirm-type="confirmationTypes.YES_NO"
    :ok-title="$t('admin-panel.change')"
    :ok-disabled="shouldDisableEditButton"
    :is-loading="loading"
    :ok-promise="saveEditedAlarms"
    :title="$t('alarm-configurations.alarm-settings')"
    @cancel="closeEditModal(false)"
  >
    <b-container class="p-0" fluid>
      <b-col
        class="px-0"
        sm="12"
        align-self="center"
      >
      </b-col>
      <b-col
        class="px-0"
        cols="12"
        align-v="center"
      >
        <b-form-group>
          <label>
            {{ $t('alarm-configurations.query.classification') }}
          </label>
          <s-select
            v-model="editOptions.alarmClassification"
            :disabled="shouldDisableClassification"
            :options="classificationTypes"
          />
        </b-form-group>
      </b-col>
      <b-col
        class="px-0"
        sm="12"
        align-self="center"
      >
        <b-alert
          class="text-center"
          variant="warning"
          :show="showAlertMessage"
        >
          <strong>{{ getAlertSetupText('title') }}</strong>
          <span v-html="getAlertSetupText('message')"></span>
        </b-alert>
      </b-col>
      <b-col
        v-if="$hasUserPermission('PER005')"
        class="alarms-classification__modal-checkbox px-0"
        sm="12"
        align-v="center"
        align-self="center"
      >
        <b-form-checkbox v-model="editOptions.displayAlarm">
          {{ $t('alarm-configurations.view-the-alarm-in-the-application') }}
          <i
            class="fas fa-info-circle alarms-classification__info-icon"
            aria-hidden="true"
            v-b-tooltip.hover.right
            :title="$t('messages.MG005.message')"
          />
        </b-form-checkbox>
      </b-col>
    </b-container>
  </s-modal>
</template>

<script>
import {
  AlarmClassificationType,
  ClassificationDefinitions,
} from 'sigma-mappings';
import ConfirmationTypes from '@/constants/confirmationTypes';
import { isSameArray } from '@/services/arrayUtils';

export default {
  name: 'AlarmEditModal',
  props: {
    value: {
      type: Boolean,
    },
    showEditAlert: {
      type: Boolean,
    },
    selectedAlarms: {
      type: Array,
      default: () => ([]),
      required: true,
    },
    editAlarmCallback: {
      type: Function,
    },
  },
  data: () => ({
    initialAlarmDisplay: null,
    editOptions: {
      displayAlarm: true,
      alarmClassification: null,
    },
    loading: false,
  }),
  computed: {
    shouldDisableClassification() {
      return !this.areAllAlarmsDefinedBySameClassification
        || this.hasSomeAlarmClassificationDefinedByChroma;
    },
    areAllAlarmsDefinedBySameClassification() {
      return this.selectedAlarms.every(
        alarm => alarm.classificationDefinedBy === this.selectedAlarms[0].classificationDefinedBy,
      );
    },
    hasSomeAlarmClassificationDefinedByChroma() {
      return this.selectedAlarms.some(alarm => alarm.classificationDefinedBy
        === ClassificationDefinitions.CHROMA_DIAGNOSIS);
    },
    hasClassification() {
      return this.editOptions.alarmClassification;
    },
    shouldDisableEditButton() {
      const hasAnotherDisplayAlarmSelected = this.initialAlarmDisplay
        !== this.editOptions.displayAlarm;
      return !this.hasClassification && !hasAnotherDisplayAlarmSelected;
    },
    alarmsIds() {
      return this.selectedAlarms.map(({ id }) => id);
    },
    sameClassificationSetup() {
      const { selectedAlarms } = this;
      const prop = 'classifications';

      return isSameArray(selectedAlarms, prop);
    },
    sameDisplaySetup() {
      const { selectedAlarms } = this;
      const prop = 'displays';

      return isSameArray(selectedAlarms, prop);
    },
    showAlertMessage() {
      return !this.sameDisplaySetup
        || !this.sameClassificationSetup
          || this.hasSomeAlarmClassificationDefinedByChroma;
    },
    sameAlarmsSetup() {
      return this.sameClassificationSetup && this.sameDisplaySetup;
    },
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        if (value && !this.sameAlarmsSetup) {
          this.editOptions.displayAlarm = true;
        }
        this.$emit('input', value);
        if (!value) this.clearOptions();
      },
    },
    classificationTypes() {
      return [
        {
          text: this.$t('alarm-configurations.query.ordinary'),
          value: AlarmClassificationType.ORDINARY,
        },
        {
          text: this.$t('alarm-configurations.query.urgent'),
          value: AlarmClassificationType.URGENT,
        },
        {
          text: this.$t('alarm-configurations.query.critical'),
          value: AlarmClassificationType.CRITICAL,
        },
      ];
    },
    confirmationTypes() {
      return ConfirmationTypes;
    },
  },
  methods: {
    getAlertSetupText(type) {
      if (this.hasSomeAlarmClassificationDefinedByChroma) {
        return this.$t(`messages.MA082.${type}`);
      }

      if (!this.sameClassificationSetup && !this.sameDisplaySetup) {
        return this.$t(`messages.MA077.${type}`);
      }

      if (!this.sameClassificationSetup) {
        return this.$t(`messages.MA015.${type}`);
      }

      if (!this.sameDisplaySetup) {
        return this.$t(`messages.MA014.${type}`);
      }

      return '';
    },
    setEditOptions(alarms) {
      this.setClassification(this.sameClassificationSetup, alarms);
      this.setDisplaySetup(this.sameDisplaySetup, alarms);
    },
    clearOptions() {
      this.editOptions = {
        displayAlarm: false,
        alarmClassification: null,
      };
    },
    saveEditedAlarms() {
      const { alarmClassification, displayAlarm } = this.editOptions;
      const payload = {
        alarmClassification,
        alarmConfigurationIds: this.alarmsIds,
      };
      if (this.$hasUserPermission('PER005')) {
        payload.displayAlarm = displayAlarm;
      }
      this.loading = true;
      return this.editAlarmCallback(payload)
        .then(() => this.closeEditModal(true))
        .finally(() => {
          this.loading = false;
        });
    },
    closeEditModal(shouldClearSelectedAlarms) {
      this.$emit('input', false);
      if (shouldClearSelectedAlarms) {
        this.$emit('clear-selected-alarms');
      }
      this.clearEditOptions();
    },
    clearEditOptions() {
      this.editOptions = {
        displayAlarm: false,
        alarmClassification: null,
        alarmConfigurationIds: [],
      };
    },
    setClassification(value, alarms) {
      if (!value) {
        this.editOptions.alarmClassification = null;
        return;
      }

      const hasOnlyOneClassification = (alarms[0].classifications || []).length === 1;
      this.editOptions.alarmClassification = hasOnlyOneClassification
        ? alarms[0].classifications[0]
        : null;
    },
    setDisplaySetup(value, alarms) {
      if (!value) {
        this.editOptions.displayAlarm = true;
        return;
      }

      const hasOnlyOneDisplay = (alarms[0].displays || []).length === 1;

      this.editOptions.displayAlarm = hasOnlyOneDisplay
        ? alarms[0].displays[0]
        : true;
      this.initialAlarmDisplay = this.editOptions.displayAlarm;
    },
  },
};
</script>

<style lang="sass" scoped>
.alarms-classification
  &__info-icon
    color: var(--color-info)
  &__modal-checkbox
    height: 60px
    display: flex
    align-items: center
</style>

<style lang="sass">
.alarm-edit-modal .modal-body
  overflow: visible
</style>
